import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { format } from 'date-fns';
import { ExamStudent } from '../../../../../../../../api/types/students/exams/student-exam';

interface ExamsTableProps {
  exams: ExamStudent[];
}

// Interface for flattened exam session rows
interface ExamSessionRow {
  examId: number;
  examName: string;
  examPoints: number | null;
  sessionId: number;
  sessionDate?: Date;
  sessionStartTime?: Date;
  sessionEndTime?: Date;
  sessionGrade?: number;
  attendance?: string;
  eligibility?: string;
  enrollment?: boolean;
  className?: string;
  highestGrade: number;
}

export const ExamsTable: React.FC<ExamsTableProps> = ({ exams }) => {
  // Flatten exams and sessions into rows
  const examSessionRows: ExamSessionRow[] = [];

  exams.forEach((exam) => {
    // Calculate highest grade across all sessions for this exam
    const grades = exam.sessions
      .map((session) => session.grade)
      .filter(
        (grade): grade is number => grade !== undefined && grade !== null,
      );
    const highestGrade = grades.length > 0 ? Math.max(...grades) : 0;

    // If exam has no sessions, create one row for the exam
    if (exam.sessions.length === 0) {
      examSessionRows.push({
        examId: exam.id,
        examName: exam.examName,
        examPoints: exam.points,
        sessionId: 0,
        highestGrade,
      });
    } else {
      // Create a row for each session
      exam.sessions.forEach((session) => {
        examSessionRows.push({
          examId: exam.id,
          examName: exam.examName,
          examPoints: exam.points,
          sessionId: session.id,
          sessionDate: session.sessionDate
            ? new Date(session.sessionDate)
            : undefined,
          sessionStartTime: session.startTime
            ? new Date(session.startTime)
            : undefined,
          sessionEndTime: session.endTime
            ? new Date(session.endTime)
            : undefined,
          sessionGrade: session.grade,
          attendance: session.attended,
          eligibility: session.examEligibility,
          enrollment: session.enrollment,
          className: session.className,
          highestGrade,
        });
      });
    }
  });

  // Template functions
  const examDateTemplate = (rowData: ExamSessionRow) => {
    if (!rowData.sessionDate) return '-';
    return format(rowData.sessionDate, 'dd/MM/yyyy');
  };

  const examTimeTemplate = (rowData: ExamSessionRow) => {
    if (!rowData.sessionStartTime) return '-';
    const startTime = format(rowData.sessionStartTime, 'HH:mm');
    const endTime = rowData.sessionEndTime
      ? format(rowData.sessionEndTime, 'HH:mm')
      : '';
    return endTime ? `${startTime} - ${endTime}` : startTime;
  };

  const examGradeTemplate = (rowData: ExamSessionRow) => {
    if (rowData.sessionGrade !== undefined && rowData.sessionGrade !== null) {
      return <span className="grade-value">{rowData.sessionGrade}</span>;
    }
    return '-';
  };

  const activeForFinalGradeTemplate = (rowData: ExamSessionRow) => {
    // Check if this session has the highest grade for this exam
    const isActive =
      rowData.sessionGrade === rowData.highestGrade &&
      rowData.sessionGrade !== undefined &&
      rowData.sessionGrade !== null;

    return isActive ? (
      <Tag value="פעיל" severity="success" icon="pi pi-check" />
    ) : (
      <Tag value="לא פעיל" severity="secondary" icon="pi pi-times" />
    );
  };

  const attendanceTemplate = (rowData: ExamSessionRow) => {
    if (!rowData.attendance) {
      return <Tag value="לא נרשם" severity="secondary" />;
    }

    switch (rowData.attendance) {
      case 'attended':
        return <Tag value="נכח" severity="success" icon="pi pi-check" />;
      case 'not_attended':
        return <Tag value="לא נכח" severity="danger" icon="pi pi-times" />;
      case 'exempt':
        return <Tag value="פטור" severity="info" icon="pi pi-star" />;
      default:
        return <Tag value="לא ידוע" severity="secondary" />;
    }
  };

  const eligibilityTemplate = (rowData: ExamSessionRow) => {
    if (!rowData.eligibility) {
      return <Tag value="לא ידוע" severity="secondary" />;
    }

    switch (rowData.eligibility) {
      case 'eligibility':
        return <Tag value="זכאי" severity="success" />;
      case 'not_eligibility':
        return <Tag value="לא זכאי" severity="danger" />;
      case 'excluded':
        return <Tag value="מוחרג" severity="warning" />;
      default:
        return <Tag value="לא ידוע" severity="secondary" />;
    }
  };

  const enrollmentTemplate = (rowData: ExamSessionRow) => {
    if (rowData.enrollment === undefined) {
      return <Tag value="לא ידוע" severity="secondary" />;
    }

    return rowData.enrollment ? (
      <Tag value="נרשם" severity="success" icon="pi pi-check" />
    ) : (
      <Tag value="לא נרשם" severity="warning" icon="pi pi-times" />
    );
  };

  return (
    <div className="dialog-exams">
      <h3>בחינות</h3>
      <DataTable
        value={examSessionRows}
        className="exams-table"
        responsiveLayout="stack"
        breakpoint="768px"
        showGridlines
        dir="rtl"
        emptyMessage="לא נמצאו בחינות"
      >
        <Column field="examName" header="שם הבחינה" sortable />
        <Column
          field="sessionDate"
          header="תאריך מועד"
          body={(rowData) => examDateTemplate(rowData)}
          sortable
        />
        <Column
          field="sessionTime"
          header="שעת מועד"
          body={(rowData) => examTimeTemplate(rowData)}
          sortable
        />
        <Column
          field="examPoints"
          header="נקודות זכות"
          body={(rowData) => rowData.examPoints || '-'}
          sortable
          dataType="numeric"
        />
        <Column
          field="sessionGrade"
          header="ציון מועד"
          body={(rowData) => examGradeTemplate(rowData)}
          sortable
          dataType="numeric"
        />
        <Column
          field="activeForFinalGrade"
          header="פעיל לציון סופי"
          body={(rowData) => activeForFinalGradeTemplate(rowData)}
          sortable
        />
        <Column
          field="attendance"
          header="נוכחות"
          body={(rowData) => attendanceTemplate(rowData)}
          sortable
        />
        <Column
          field="eligibility"
          header="זכאות"
          body={(rowData) => eligibilityTemplate(rowData)}
          sortable
        />
        <Column
          field="enrollment"
          header="רישום"
          body={(rowData) => enrollmentTemplate(rowData)}
          sortable
        />
        <Column
          field="className"
          header="כיתה"
          body={(rowData) => rowData.className || '-'}
          sortable
        />
      </DataTable>
    </div>
  );
};
